import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import type { WorkspaceSummaryResponse } from "../../../../shared/api-types";
import { FEATURE_FLAGS } from "../../../../shared/features";
import { authenticatedApi } from "@/lib/api";
import { showsChatsCard, type ChatsCardSurface } from "@/lib/rd-chats";
import { summaryForRd } from "@/lib/rd-page-tabs";
import { useFeatures } from "@/lib/use-features";
import {
  RdChatsCard,
  type RdChatsCardState,
  type RenderRdChatsCardLink,
} from "./rd-chats-card";
import { useShellFrame } from "./shell-layout";

// Where the RD page's Chats card mounts (NO-754). A surface names itself, and
// the card shows only in the rail frame and only on the surface
// `chatsCardSurface` picks: Overview while supervisor-metrics is on, Sessions
// while it is off. Nothing shows until the flag read settles, so the card
// never flashes on Sessions and then moves. The summary is the RD page's own
// ["workspace-summary", <url param>] entry, so it loads once per RD.

function cardState(
  summary: WorkspaceSummaryResponse | undefined,
  failed: boolean,
  rdParam: string
): RdChatsCardState {
  const own = summaryForRd(summary, rdParam, rdParam);

  if (own) {
    return { status: "ready", threads: own.threads, otherOwners: own.otherOwners };
  }

  return failed ? { status: "error" } : { status: "loading" };
}

// Another owner's thread opens read-only: the thread route serves it to a
// `chats:read:all` viewer and the chat renders it without a composer.
function cardLinkRenderer(rdParam: string): RenderRdChatsCardLink {
  return ({ target, className, children }) =>
    target.to === "/rds/$rdId/chats/new" ? (
      <Link to={target.to} params={{ rdId: rdParam }} className={className}>
        {children}
      </Link>
    ) : (
      <Link
        to={target.to}
        params={{ rdId: rdParam, chatId: target.chatId }}
        className={className}
      >
        {children}
      </Link>
    );
}

function useChatsCardShown(surface: ChatsCardSurface): boolean {
  const frame = useShellFrame();
  const features = useFeatures();
  const metricsEnabled = features.data?.data[FEATURE_FLAGS.supervisorMetrics] === true;

  return (
    features.status !== "pending" && showsChatsCard({ frame, metricsEnabled, surface })
  );
}

export function RdChatsCardSlot({
  rdParam,
  rdName,
  surface,
}: {
  rdParam: string;
  rdName: string;
  surface: ChatsCardSurface;
}): ReactElement | null {
  const { getToken } = useAuth();
  const shown = useChatsCardShown(surface);
  const summary = useQuery({
    queryKey: ["workspace-summary", rdParam],
    queryFn: () =>
      authenticatedApi<{ data: WorkspaceSummaryResponse }>(
        getToken,
        `/api/rds/${rdParam}/chats`
      ),
    enabled: shown,
  });
  if (!shown) return null;

  return (
    <RdChatsCard
      rdName={rdName}
      state={cardState(summary.data?.data, summary.isError, rdParam)}
      renderLink={cardLinkRenderer(rdParam)}
    />
  );
}
